import {
  faDownload,
  faRotateRight,
  faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { Circle, Group, Image, Layer, Rect, Stage, Text } from "react-konva";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import closeButton from "../../../../../assets/images/close-button.png";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import { dataUrlToFile } from "../../../../../shared/helpers/data-url-to-file";
import { safeAtob } from "../../../../../shared/helpers/enode-decode-string";
import { FloorShape } from "../../../../../shared/models/floorPlan";
import "./floorPlan.scss";

import gatewayFault from "./images/gateway-fault-filled.png";
import gatewayInitial from "./images/gateway-initial-filled.png";
import gatewayOffline from "./images/gateway-offline-filled.png";
import gatewayOnline from "./images/gateway-online-filled.png";
import photocellOnline from "./images/PhotocellSensorGreen.png";
import photocellOffline from "./images/PhotocellSensorGrey.png";
import photocellOff from "./images/PhotocellSensorRed.png";

import motion26to50 from "./images/26-to-50.png";
import motion51to75 from "./images/51-to-75.png";
import motion0to25 from "./images/less-than-26.png";
import motionMoreThan75 from "./images/more-than-75.png";

import circleSelectedLight from "./images/circle-light-selected.png";

import { faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { B2Form, B2Switch } from "@hdb/biome-2";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import {
  getLastDaylightReadingByAsset,
  getLastMotionReadingByAsset,
} from "src/core/api/services/asset.service";
import { getAssetWiseLastHourMotion } from "src/core/api/services/dashbaorad-details/floor/floor.service";
import { DynamicValuesEnum } from "src/core/enums/dynamic-values.enum";
import { heatmapColors } from "src/core/enums/floorplan-color-enums";
import { formatDateTime } from "src/shared/helpers/convert-time-format";
import { calculateAllDynamicValues } from "src/shared/helpers/dynamic-values.helper";
import {
  calculateImageDimensions,
  createPinchZoomHandlers,
  getZoomStepIn,
  getZoomStepOut,
  handleShapeMouseEnter,
  handleShapeMouseLeave,
  handleStageWheel as handleStageWheelHelper,
} from "src/shared/helpers/floorplan.helpers";
import { calculateLabelLayouts } from "src/shared/helpers/label-layout.helper";
import {
  exportStageToPDF,
  generatePDFFileName,
} from "src/shared/helpers/pdf-export.helper";
import { getLocationBlockByCode } from "src/store/slices/location-block.slice";
import exitFullScreen from "../../../../../assets/images/arrows-collapse.png";
import fullScreen from "../../../../../assets/images/arrows-fullscreen.png";
import zoomIn from "../../../../../assets/images/zoom-in.png";
import zoomOut from "../../../../../assets/images/zoom-out.png";
import useOutsideClick from "../hooks/useOutsideClick";

const FloorPlanHeatmapComponent = ({
  // Color scheme for motion count circles
  imageFile,
  responseShapes,
  isLoading,
  highlightedAsset, // hightlight the asset which is searched from the search bar
  setSelectedAssetCodeForShape,
  isProcessingImage,
}: any) => {
  const stageRef: any = useRef();
  const pinchStateRef: any = useRef(null);
  const params = useParams();
  const blockCode: any = params.blockCode ? safeAtob(params.blockCode) : null;
  const dispatch = useDispatch();

  const [isLoadingMotionAndDaylightValue, setIsLoadingMotionAndDaylightValue] =
    useState(false);

  let { data: assetWiseMotion } = useQuery({
    queryKey: ["getAssetWiseLastHourMotion", blockCode, params?.floor],
    queryFn: ({ signal }) =>
      getAssetWiseLastHourMotion(blockCode, params?.floor, signal),
    enabled: !!blockCode && !!params?.floor,
  });

  // Gateway
  const file1 = "image1.png";
  const gatewayFileFault: any = useMemo(
    () => dataUrlToFile(gatewayFault, file1),
    [],
  );

  const file2 = "image1.png";
  const gatewayFileInitial: any = useMemo(
    () => dataUrlToFile(gatewayInitial, file2),
    [],
  );

  const file3 = "image1.png";
  const gatewayFileOffline: any = useMemo(
    () => dataUrlToFile(gatewayOffline, file3),
    [],
  );

  const file4 = "image1.png";
  const gatewayFileOnline: any = useMemo(
    () => dataUrlToFile(gatewayOnline, file4),
    [],
  );

  const file5 = "image5.png";
  const photocellFileOnline: any = useMemo(
    () => dataUrlToFile(photocellOnline, file5),
    [],
  );

  const file6 = "image6.png";
  const photocellFileOffline: any = useMemo(
    () => dataUrlToFile(photocellOffline, file6),
    [],
  );

  const file7 = "image7.png";
  const photocellFileOff: any = useMemo(
    () => dataUrlToFile(photocellOff, file7),
    [],
  );

  const filename10 = "image10.png";
  const selectedFile: any = useMemo(
    () => dataUrlToFile(circleSelectedLight, filename10),
    [],
  );

  const motion1File = "motion1.png";
  const motion0to25File: any = useMemo(
    () => dataUrlToFile(motion0to25, motion1File),
    [],
  );

  const motion2File = "motion2.png";
  const motion26to50File: any = useMemo(
    () => dataUrlToFile(motion26to50, motion2File),
    [],
  );

  const motion3File = "motion3.png";
  const motion51to75File: any = useMemo(
    () => dataUrlToFile(motion51to75, motion3File),
    [],
  );

  const motion4File = "motion4.png";
  const motionMoreThan75File: any = useMemo(
    () => dataUrlToFile(motionMoreThan75, motion4File),
    [],
  );

  const [globalSelectedShape, setGlobalSelectedShape] = useState(null as any);
  const [selectedWhileHovered, setSelectedWhileHovered] = useState(null as any);

  // const [image, setImage] = useState<any>(null);

  const [gatewayImageFileFault, setGatewayImageFileFault] =
    useState<any>(undefined);
  const [gatewayImageFileOffline, setGatewayImageFileOffline] =
    useState<any>(undefined);
  const [gatewayImageFileInitial, setGatewayImageFileInitial] =
    useState<any>(undefined);
  const [gatewayImageFileOnline, setGatewayImageFileOnline] =
    useState<any>(undefined);
  const [photocellImageFileOnline, setPhotocellImageFileOnline] =
    useState<any>(undefined);
  const [photocellImageFileOffline, setPhotocellImageFileOffline] =
    useState<any>(undefined);
  const [photocellImageFileOff, setPhotocellImageFileOff] =
    useState<any>(undefined);

  /*   const [onOfflineImageFile, setOnOfflineImageFile] = useState<any>(undefined);
  const [onOnlineImageFile, setOnOnlineImageFile] = useState<any>(undefined); */
  const [motion0to25ImageFile, setMotion0to25ImageFile] =
    useState<any>(undefined);
  const [motion26to50ImageFile, setMotion26to50ImageFile] =
    useState<any>(undefined);
  const [motion51to75ImageFile, setMotion51to75ImageFile] =
    useState<any>(undefined);
  const [motionMoreThan75ImageFile, setMotionMoreThan75ImageFile] =
    useState<any>(undefined);
  const [selectedImageFile, setSelectedImageFile] = useState<any>(undefined);

  const [hovered, setHovered] = useState(false);

  const [showLabels, setShowLabels] = useState(false);
  const [showAssets, setShowAssets] = useState(true);
  const [showLayerDropdown, setShowLayerDropdown] = useState(false);
  // Toggle to enable/disable label overlap avoidance
  const [preventLabelOverlap, setPreventLabelOverlap] = useState(false);

  const [shapes, setShapes] = useState<FloorShape[]>([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [newValueOnStageMove, setNewValueOnStageMove] = useState({
    x: 0,
    y: 0,
  });

  // Update calculated values when zoom level changes
  useEffect(() => {
    // Calculate all dynamic values using the helper function
    const dynamicValues = calculateAllDynamicValues(zoomLevel);

    // Update gateway size and offset
    setDynamicGateway(dynamicValues.gateway.size);
    setDynamicGatewayOffset(dynamicValues.gateway.offset);
    setDynamicGatewayLabelOffset(dynamicValues.gateway.labelOffset);
    setDynamicPhotocell(dynamicValues.photocell.size);
    setDynamicPhotocellOffset(dynamicValues.photocell.offset);
    setDynamicPhotocellLabelOffset(dynamicValues.photocell.labelOffset);

    // Update circle size and offset
    setDynamicCircle(dynamicValues.circle);
    setCirclePositionOffset(dynamicValues.circlePositionOffset);
    setLabelDynamics(dynamicValues.labelDynamics);
  }, [zoomLevel]);

  const handleStageDrag = (x: any, y: any) => {
    setNewValueOnStageMove({ x, y });

    if (tooltip?.visible && globalSelectedShape) {
      const baseX = (globalSelectedShape?.x ?? 0);
      const baseY = (globalSelectedShape?.y ?? 0);
      const adjustedX = (baseX + x) * zoomLevel;
      const adjustedY = (baseY + y) * zoomLevel;

      setTooltip({
        ...tooltip,
        x: adjustedX,
        y: adjustedY - 10,
      });
    }
  };

  const [containerDimensions, setContainerDimensions] = useState({
    width: 942,
    height: 530,
  });

  const [tooltip, setTooltip] = useState({
    visible: false,
    content: "",
    selectedItemId: null,
    x: 0,
    y: 0,
  });

  const [hoverState, setHoverState] = useState({
    visible: false,
    x: 0,
    y: 0,
  });

  // Hover timer to show hover tooltip only after a short delay
  const hoverTimeoutRef = useRef<number | null>(null);
  const HOVER_DELAY = 500; // milliseconds

  // Cleanup hover timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  // Dynamic sizing state variables
  const [dynamicGateway, setDynamicGateway] = useState(
    DynamicValuesEnum.GATEWAY_SIZE,
  );
  const [dynamicGatewayOffset, setDynamicGatewayOffset] = useState(0);
  const [dynamicGatewayLabelOffset, setDynamicGatewayLabelOffset] = useState(
    DynamicValuesEnum.GATEWAY_LABEL_OFFSET,
  );
  const [dynamicPhotocell, setDynamicPhotocell] = useState(
    DynamicValuesEnum.PHOTOCELL_SIZE,
  );
  const [dynamicPhotocellOffset, setDynamicPhotocellOffset] = useState(0);
  const [dynamicPhotocellLabelOffset, setDynamicPhotocellLabelOffset] =
    useState(DynamicValuesEnum.PHOTOCELL_LABEL_OFFSET);

  // Calculated values based on zoom level
  const [dynamicCircle, setDynamicCircle] = useState(DynamicValuesEnum.CIRCLE);
  const [circlePositionOffset, setCirclePositionOffset] = useState(0);

  // Label dynamics object containing label-related properties
  const [labelDynamics, setLabelDynamics] = useState({
    fontSize: DynamicValuesEnum.FONT_SIZE,
    height: DynamicValuesEnum.LABEL_HEIGHT,
    yOffset: DynamicValuesEnum.LABEL_Y_OFFSET,
    padding: DynamicValuesEnum.LABEL_PADDING,
    textPadding: DynamicValuesEnum.TEXT_PADDING,
    strokeWidth: DynamicValuesEnum.STROKE_WIDTH,
    cornerRadius: DynamicValuesEnum.CORNER_RADIUS,
    shadowBlur: DynamicValuesEnum.SHADOW_BLUR,
    shadowColor: DynamicValuesEnum.SHADOW_COLOR,
    shadowOffset: DynamicValuesEnum.SHADOW_OFFSET,
  });

  /*   useEffect(() => {
    setImage(null);
    if (!imageFile) return;
    // If imageFile is a base64 string, create a data URL
    const isBase64 =
      typeof imageFile === "string" && imageFile.startsWith("iVBOR");
    const src = isBase64 ? `data:image/png;base64,${imageFile}` : imageFile;

    const imageElement = new window.Image();
    imageElement.src = src;
    imageElement.onload = () => setImage(imageElement);
  }, [imageFile]); */

  // gateway
  useEffect(() => {
    const imageUrl = URL.createObjectURL(gatewayFileFault);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setGatewayImageFileFault(imageElement);
  }, [gatewayFileFault]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(gatewayFileInitial);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setGatewayImageFileInitial(imageElement);
  }, [gatewayFileInitial]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(gatewayFileOffline);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setGatewayImageFileOffline(imageElement);
  }, [gatewayFileOffline]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(gatewayFileOnline);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setGatewayImageFileOnline(imageElement);
  }, [gatewayFileOnline]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(photocellFileOnline);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setPhotocellImageFileOnline(imageElement);
  }, [photocellFileOnline]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(photocellFileOffline);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setPhotocellImageFileOffline(imageElement);
  }, [photocellFileOffline]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(photocellFileOff);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setPhotocellImageFileOff(imageElement);
  }, [photocellFileOff]);

  // light on

  useEffect(() => {
    const imageUrl = URL.createObjectURL(motion0to25File);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setMotion0to25ImageFile(imageElement);
  }, [motion0to25File]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(motion26to50File);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setMotion26to50ImageFile(imageElement);
  }, [motion26to50File]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(motion51to75File);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setMotion51to75ImageFile(imageElement);
  }, [motion51to75File]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(motionMoreThan75File);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setMotionMoreThan75ImageFile(imageElement);
  }, [motionMoreThan75File]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(selectedFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setSelectedImageFile(imageElement);
  }, [selectedFile]);

  useEffect(() => {
    if (responseShapes?.length === 0) {
      setShapes([]);
      return;
    }

    if (assetWiseMotion?.length === 0) {
      setShapes(responseShapes);
    } else {
      const updatedShape = responseShapes
        ?.map((shape: FloorShape) => {
          const matchedAssets = assetWiseMotion?.find(
            (s: any) =>
              s.assetCode === shape.assetCode ||
              shape.assetType.toLowerCase() === "gateway" ||
              shape.assetType.toLowerCase() === "photocell sensor",
          );
          return matchedAssets
            ? {
              ...shape,
              motionCount: matchedAssets.motionCount,
              highlightedAsset:
                matchedAssets.assetCode === highlightedAsset ? true : false,
            }
            : null;
        })
        .filter(
          (shape: FloorShape | null): shape is FloorShape => shape !== null,
        );

      //  ;
      setShapes(updatedShape);
    }
  }, [responseShapes, assetWiseMotion, highlightedAsset]);

  useEffect(() => {
    return () => {
      if (typeof setSelectedAssetCodeForShape === "function") {
        setSelectedAssetCodeForShape(""); // Reset the state
      }
    };
  }, []);

  // handle image when height is more than width
  const { width, height, assetScale } = useMemo(
    () => calculateImageDimensions(imageFile),
    [imageFile],
  );

  const scaledShapes = useMemo(() => {
    return shapes?.map((shape: FloorShape) => ({
      ...shape,
      x: (shape?.x ?? 0) * assetScale?.x,
      y: (shape?.y ?? 0) * assetScale?.y,
    }));
  }, [shapes, assetScale]);

  // Pre-calculated, non-overlapping label layouts keyed by shape id
  const labelLayouts = useMemo(() => {
    return calculateLabelLayouts({
      showLabels,
      showAssets,
      shapes: scaledShapes,
      labelDynamics,
      preventLabelOverlap,
      dynamicCircle,
    });
  }, [
    showLabels,
    showAssets,
    scaledShapes,
    labelDynamics.fontSize,
    labelDynamics.height,
    labelDynamics.padding,
    labelDynamics.yOffset,
    preventLabelOverlap,
    dynamicCircle,
  ]);

  const handleZoomIn = () => {
    setZoomLevel(
      Math.min(
        DynamicValuesEnum.MAX_ZOOM,
        zoomLevel + getZoomStepIn(zoomLevel),
      ),
    );

    setTooltip({ ...tooltip, visible: false });
    setHoverState({ ...hoverState, visible: false });
    setGlobalSelectedShape(null);
    setSelectedWhileHovered(null);
  };

  const handleZoomOut = () => {
    setZoomLevel(Math.max(0.5, zoomLevel - getZoomStepOut(zoomLevel)));

    setTooltip({ ...tooltip, visible: false });

    setHoverState({ ...hoverState, visible: false });
    setGlobalSelectedShape(null);
    setSelectedWhileHovered(null);
  };

  // // Wheel zoom while hovering the stage — delegating to shared helper
  const handleStageWheel = (e: any) => {
    handleStageWheelHelper({
      e,
      stageRef,
      zoomLevel,
      setZoomLevel,
      newValueOnStageMove,
      setNewValueOnStageMove,
      setTooltip,
      tooltip,
      setHoverState,
      hoverState,
      setGlobalSelectedShape,
      setSelectedWhileHovered,
      dynamicValuesEnumMaxZoom: DynamicValuesEnum.MAX_ZOOM,
    });
  };

  // Touch handlers for smooth pinch-to-zoom / two-finger pan on mobile
  const { handleTouchStart, handleTouchMove, handleTouchEnd } =
    createPinchZoomHandlers(
      {
        stageRef,
        zoomLevel,
        setZoomLevel,
        dynamicValuesEnumMaxZoom: DynamicValuesEnum.MAX_ZOOM,
        dynamicValuesEnumMinZoom: 0.5,
        setNewValueOnStageMove,
      },
      pinchStateRef,
    );


  // Reset zoom and re-center the stage layer to top-left
  const handleResetZoom = () => {
    setZoomLevel(1);
    setNewValueOnStageMove({ x: 0, y: 0 });

    const stage: any = stageRef.current;
    if (stage) {
      try {
        const layers = stage.getLayers ? stage.getLayers() : [];
        const layer = layers && layers.length ? layers[0] : null;
        if (layer) {
          layer.position({ x: 0, y: 0 });
          layer.batchDraw ? layer.batchDraw() : layer.draw();
        }
      } catch (err) {
        // fail silently if Konva refs not ready
        // eslint-disable-next-line no-console
        console.warn("Could not reset layer position:", err);
      }
    }

    setTooltip({ ...tooltip, visible: false });
    setHoverState({ ...hoverState, visible: false });
    setGlobalSelectedShape(null);
    setSelectedWhileHovered(null);
  };

  const handleOnClick = async (id: any, e?: any) => {
    const selectedShape = shapes?.find((shape) => shape?.id === id);
    if (!selectedShape) {
      setIsLoadingMotionAndDaylightValue(false);
      return;
    }

    if (selectedShape) {
      const parentAssetTypeId = selectedShape?.parentAssetTypeId?.toLowerCase();

      // "1" and "44" for photocell sensor, "3" for gateway
      const offset =
        parentAssetTypeId === "3"
          ? dynamicGatewayOffset
          : parentAssetTypeId === "1" || parentAssetTypeId === "44"
            ? dynamicPhotocellOffset
            : circlePositionOffset;

      const baseX = selectedShape?.x ? selectedShape?.x * assetScale?.x : 0;
      const baseY = selectedShape?.y ? selectedShape?.y * assetScale?.y : 0;

      const adjustedX =
        (baseX + newValueOnStageMove?.x + offset) * zoomLevel;
      const adjustedY =
        (baseY + newValueOnStageMove?.y + offset) * zoomLevel;

      setTooltip({
        visible: true,
        content: `Asset ID: ${selectedShape.assetCode}\nLocation: ${selectedShape.floorLocation}\nType: ${selectedShape.assetType}\nLast Heartbeat: ${selectedShape.lastOnline}`,
        selectedItemId: id,
        x: handle.active && e ? e?.evt?.layerX - 18 : adjustedX,
        y: handle.active && e ? e?.evt?.layerY - 28 : adjustedY,
      });

      setGlobalSelectedShape(selectedShape);

      /*       dispatch(
        sendGetCommandToDms({
          assetCode: selectedShape.assetCode,
          module: Number(AssetCommandEnums.getLightIntensity),
        })
      ); */

      try {
        const [motionData, daylightData] = await Promise.all([
          getLastMotionReadingByAsset(selectedShape.assetCode, null),
          getLastDaylightReadingByAsset(selectedShape.assetCode, null),
        ]);

        const updatedShape = {
          ...selectedShape,
          motionCount: motionData?.data?.motionReading
            ? motionData?.data?.motionReading
            : selectedShape.motionCount,
          daylight: daylightData?.data?.daylightReading
            ? daylightData?.data?.daylightReading
            : selectedShape.daylight,
        };

        setGlobalSelectedShape(updatedShape);
      } catch (error) {
        console.error("Error fetching motion/daylight data:", error);
        setGlobalSelectedShape(selectedShape);
      } finally {
        setIsLoadingMotionAndDaylightValue(false);
      }
    }
  };

  const dimensions = () => {
    const totalWidthElement = document.getElementById("totalWidth");

    setContainerDimensions({
      height: 530,
      width: totalWidthElement?.offsetWidth
        ? totalWidthElement?.offsetWidth - 100
        : 942,
    });
  };

  useEffect(() => {
    dimensions();
  }, []);

  const handle = useFullScreenHandle();

  const handleFullScreenClose = (currentState: boolean) => {
    if (!currentState) {
      setZoomLevel(1);
      dimensions();
      setGlobalSelectedShape(null);
      setSelectedWhileHovered(null);
    }
  };

  const handleAssetClick = () => {
    const newTab = window.open(
      `/asset/${btoa(globalSelectedShape?.assetCode)}`,
      "_blank",
    );
    newTab?.focus();
  };

  const [isHoveredTooltipElement, setIsHoveredTooltipElement] = useState(false);

  // Export settings (DPI for high-resolution exports)
  const [exportDPI, setExportDPI] = useState(600);

  const blockDetail = useSelector(
    (state: any) => state.locationBlock.locationBlockDetails,
  );
  const isLoadingLocationBlockDetails = useSelector(
    (state: any) => state.locationBlock.isLoadingLocationBlockDetails,
  );

  const closeToolTip = () => {
    if (!isHoveredTooltipElement && !hovered) {
      setTooltip({ ...tooltip, visible: false });
      setGlobalSelectedShape(null);
    }
  };

  const { ref: toolTipRef, isClickedOutside: isOutside } =
    useOutsideClick(closeToolTip);

  const handleExportPDF = async (
    includeLabels: boolean,
    includeAssets: boolean,
  ) => {
    const stage = stageRef.current;
    if (!stage) return;

    try {
      const fileName = generatePDFFileName(
        blockDetail,
        params.floor || "Unknown",
      );

      await exportStageToPDF({
        stage,
        includeLabels,
        includeAssets,
        exportDPI,
        fileName,
        shapes: shapes,
        type: "heatmap",
      });
    } catch (error) {
      console.error("Error exporting PDF:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".layer-dropdown") &&
        !target.closest(".fa-layer-group")
      ) {
        setShowLayerDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="w-100" id="totalWidth">
      <SkeletonComponent
        wrapperHeight="570px"
        skeletonHeight="570px"
        isLoading={isLoading || isProcessingImage}
      >
        {imageFile &&
          shapes &&
          gatewayImageFileFault &&
          gatewayImageFileOffline &&
          gatewayImageFileInitial &&
          gatewayImageFileOnline &&
          photocellImageFileOnline &&
          photocellImageFileOffline &&
          photocellImageFileOff &&
          motion0to25ImageFile &&
          motion26to50ImageFile &&
          motion51to75ImageFile &&
          motionMoreThan75ImageFile &&
          selectedImageFile ? (
          <FullScreen
            handle={handle}
            onChange={(state: boolean) => {
              handleFullScreenClose(state);
            }}
          >
            <div className="mt-3 position-relative w-100">
              <Stage
                width={containerDimensions.width}
                height={containerDimensions.height}
                scaleX={zoomLevel}
                scaleY={zoomLevel}
                ref={stageRef}
                onWheel={handleStageWheel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className={`my-3 ${handle.active ? "" : "stage-wrapper"} `}
              >
                <Layer
                  y={15}
                  draggable
                  onDragEnd={(e) => handleStageDrag(e.target.x(), e.target.y())}
                >
                  {imageFile && containerDimensions?.height > 0 && (
                    <Image
                      name="floor-image"
                      image={imageFile}
                      width={width}
                      height={height}
                    />
                  )}
                  {showAssets &&
                    scaledShapes?.map((shape: FloorShape) => {
                      // "1" and "44" for photocell sensor, "3" for gateway
                      const parentAssetTypeId = shape?.parentAssetTypeId?.toLowerCase();
                      const isGateway = parentAssetTypeId === "3";
                      const isPhotocell = parentAssetTypeId === "1" || parentAssetTypeId === "44";

                      return (
                        <React.Fragment key={shape.id}>
                          {isGateway || isPhotocell ? (
                            <>
                              {isGateway && (
                                <Rect
                                  x={shape.x - 7} // Adjust position and size as needed
                                  y={shape.y - 5.5} // Adjust position and size as needed
                                  width={30}
                                  height={30}
                                  fillRadialGradientStartPoint={{
                                    x: 15,
                                    y: 15,
                                  }} // Center of the gradient
                                  fillRadialGradientEndPoint={{ x: 15, y: 15 }} // Center of the gradient
                                  fillRadialGradientStartRadius={0} // Radius at the start point
                                  fillRadialGradientEndRadius={15} // Radius at the end point
                                  // fillRadialGradientColorStops={getColorStops(
                                  //   shape.motionCount
                                  // )} // Gradient colors
                                  cornerRadius={50} // Optional: Add rounded corners
                                />
                              )}
                              <Image
                                name={
                                  isGateway
                                    ? "gateway-image"
                                    : "photocell-image"
                                }
                                x={
                                  shape.x +
                                  (isGateway
                                    ? dynamicGatewayOffset
                                    : dynamicPhotocellOffset)
                                }
                                y={
                                  shape.y +
                                  (isGateway
                                    ? dynamicGatewayOffset
                                    : dynamicPhotocellOffset)
                                }
                                image={
                                  shape?.highlightedAsset
                                    ? selectedImageFile
                                    : isGateway
                                      ? shape?.status === "On" ||
                                        shape?.status === "Online"
                                        ? gatewayImageFileOnline
                                        : shape?.status === "N/A" ||
                                          shape?.status === "Offline" ||
                                          shape?.status === "Off"
                                          ? gatewayImageFileInitial
                                          : gatewayImageFileFault
                                      : shape?.status === "On" ||
                                        shape?.status === "Online"
                                        ? photocellImageFileOnline
                                        : shape?.status === "N/A" ||
                                          shape?.status === "Offline" ||
                                          shape?.status === "Off"
                                          ? photocellImageFileOffline
                                          : photocellImageFileOff
                                }
                                width={
                                  isGateway
                                    ? dynamicGateway
                                    : dynamicPhotocell
                                }
                                height={
                                  isGateway
                                    ? dynamicGateway
                                    : dynamicPhotocell
                                }
                                onClick={(e: any) => {
                                  setIsLoadingMotionAndDaylightValue(true);
                                  handleOnClick(shape?.id, e);
                                }}
                                onTap={(e: any) => {
                                  setIsLoadingMotionAndDaylightValue(true);
                                  handleOnClick(shape?.id, e);
                                }}
                                onMouseEnter={(e: any) => {
                                  handleShapeMouseEnter({
                                    e,
                                    shape,
                                    shapes: scaledShapes,
                                    hoverTimeoutRef,
                                    HOVER_DELAY,
                                    setHovered,
                                    setSelectedWhileHovered,
                                    setHoverState,
                                    // newValueOnStageMove,
                                    // zoomLevel,
                                    // dynamicGatewayOffset,
                                    // circlePositionOffset,
                                  });
                                }}
                                onMouseLeave={(e: any) =>
                                  handleShapeMouseLeave({
                                    e,
                                    hoverTimeoutRef,
                                    setHovered,
                                    setHoverState,
                                    setSelectedWhileHovered,
                                  })
                                }
                              />
                              {showLabels &&
                                shape?.labelDisplayName &&
                                (() => {
                                  const layout = labelLayouts.get(shape.id);
                                  if (!layout) return null;
                                  return (
                                    <Group
                                      x={layout.x}
                                      y={
                                        layout.y +
                                        (isGateway
                                          ? dynamicGatewayLabelOffset
                                          : dynamicPhotocellLabelOffset)
                                      }
                                      name="sensor-label"
                                      listening={false}
                                    >
                                      <Rect
                                        width={layout.w}
                                        height={layout.h}
                                        fill="#fff"
                                        stroke="#bdbdbd"
                                        strokeWidth={labelDynamics.strokeWidth}
                                        cornerRadius={labelDynamics.cornerRadius}
                                        shadowColor={`rgba(0,0,0,${labelDynamics.shadowColor})`}
                                        shadowBlur={labelDynamics.shadowBlur}
                                        shadowOffset={{
                                          x: labelDynamics.shadowOffset,
                                          y: labelDynamics.shadowOffset,
                                        }}
                                      />
                                      <Text
                                        text={shape?.labelDisplayName}
                                        fontSize={labelDynamics.fontSize}
                                        fill="#333"
                                        fontStyle="bold"
                                        padding={labelDynamics.textPadding}
                                        align="left"
                                      />
                                    </Group>
                                  );
                                })()}
                            </>
                          ) : (
                            <>
                              {/* <Rect
                              x={shape.x - 4} // Adjusted position to better center the gradient
                              y={shape.y - 3.5} // Adjusted position to better center the gradient
                              width={20} // Adjusted width
                              height={20} // Adjusted height
                              fillRadialGradientStartPoint={{
                                x: 10,
                                y: 10,
                              }} // Center of the gradient
                              fillRadialGradientEndPoint={{ x: 10, y: 10 }} // Center of the gradient
                              fillRadialGradientStartRadius={0} // Radius at the start point
                              fillRadialGradientEndRadius={10} // Radius at the end point
                              fillRadialGradientColorStops={getColorStops(
                                shape.motionCount
                              )} // Gradient colors
                              cornerRadius={30} // Optional: Add rounded corners
                            /> */}

                              {/* white circular background so gradient renders on white (no square) */}
                              {/* <Circle
                            name="asset-circle-bg"
                            x={shape.x + circlePositionOffset + dynamicCircle / 2}
                            y={shape.y + circlePositionOffset + dynamicCircle / 2}
                            radius={dynamicCircle / 2}
                            fill="#fff"
                            strokeWidth={0}
                            listening={false}
                          /> */}

                              <Circle
                                name="asset-circle"
                                x={
                                  shape.x +
                                  circlePositionOffset +
                                  dynamicCircle / 2
                                }
                                y={
                                  shape.y +
                                  circlePositionOffset +
                                  dynamicCircle / 2
                                }
                                radius={dynamicCircle / 2}
                                fillRadialGradientEndRadius={dynamicCircle / 2}
                                fillRadialGradientColorStops={
                                  shape?.highlightedAsset
                                    ? heatmapColors.selected
                                    : shape?.motionCount < 26
                                      ? heatmapColors.motion0to25
                                      : shape?.motionCount > 25 &&
                                        shape?.motionCount < 51
                                        ? heatmapColors.motion26to50
                                        : shape?.motionCount > 50 &&
                                          shape?.motionCount < 76
                                          ? heatmapColors.motion51to75
                                          : heatmapColors.motionMoreThan75
                                }
                                stroke={heatmapColors.border}
                                strokeWidth={0.2}
                                onClick={(e: any) => {
                                  setIsLoadingMotionAndDaylightValue(true);
                                  handleOnClick(shape?.id, e);
                                }}
                                onTap={(e: any) => {
                                  setIsLoadingMotionAndDaylightValue(true);
                                  handleOnClick(shape?.id, e);
                                }}
                                onMouseEnter={(e: any) => {
                                  handleShapeMouseEnter({
                                    e,
                                    shape,
                                    shapes: scaledShapes,
                                    hoverTimeoutRef,
                                    HOVER_DELAY,
                                    setHovered,
                                    setSelectedWhileHovered,
                                    setHoverState,
                                    // newValueOnStageMove,
                                    // zoomLevel,
                                    // dynamicGatewayOffset,
                                    // circlePositionOffset,
                                  });
                                }}
                                onMouseLeave={(e: any) =>
                                  handleShapeMouseLeave({
                                    e,
                                    hoverTimeoutRef,
                                    setHovered,
                                    setHoverState,
                                    setSelectedWhileHovered,
                                  })
                                }
                              />
                              {showLabels &&
                                shape?.labelDisplayName &&
                                (() => {
                                  const layout = labelLayouts.get(shape.id);
                                  if (!layout) return null;
                                  return (
                                    <Group
                                      x={layout.x}
                                      y={layout.y}
                                      name="sensor-label"
                                      listening={false}
                                    >
                                      <Rect
                                        width={layout.w}
                                        height={layout.h}
                                        fill="#fff"
                                        stroke="#bdbdbd"
                                        strokeWidth={labelDynamics.strokeWidth}
                                        cornerRadius={labelDynamics.cornerRadius}
                                        shadowColor={`rgba(0,0,0,${labelDynamics.shadowColor})`}
                                        shadowBlur={labelDynamics.shadowBlur}
                                        shadowOffset={{
                                          x: labelDynamics.shadowOffset,
                                          y: labelDynamics.shadowOffset,
                                        }}
                                      />
                                      <Text
                                        text={shape?.labelDisplayName}
                                        fontSize={labelDynamics.fontSize}
                                        fill="#333"
                                        fontStyle="bold"
                                        padding={labelDynamics.textPadding}
                                        align="center"
                                      />
                                    </Group>
                                  );
                                })()}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                </Layer>
              </Stage>

              {tooltip?.visible &&
                globalSelectedShape &&
                shapes?.find(
                  (shape) => shape.id === tooltip.selectedItemId,
                ) && (
                  <div
                    ref={toolTipRef}
                    onMouseEnter={() => setIsHoveredTooltipElement(true)}
                    onMouseLeave={() => setIsHoveredTooltipElement(false)}
                    style={{
                      position: "absolute",
                      top: tooltip.y - 210,
                      left: tooltip.x - 157,
                      zIndex: 9999,
                      width: "350px",
                    }}
                  >
                    <div
                      className="floortooltip"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        height:
                          globalSelectedShape?.assetTypeId != 3
                            ? "200px"
                            : "180px",
                        width: "max-content",
                        zIndex: "999",
                        position: "relative",
                        borderRadius: "5px",
                        backgroundColor: "white",
                        boxShadow:
                          "0 -3px 3px -3px #e0e0e0, 3px 0px 3px -3px #d1d1d1, 5px 5px 3px -5px #e5e5e5",
                        padding: " 0.5rem 1rem",
                        lineHeight: "25px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div className="d-flex">
                          <div className="tooltip-fs">Asset ID: </div>
                          <button
                            type="button"
                            style={{ 
                              color: "#1976D2", 
                              cursor: "pointer",
                              padding: 0,
                              border: "none",
                              backgroundColor: "transparent", 
                            }}
                            onClick={handleAssetClick}
                            className="ms-2 tooltip-fs"
                          >
                            {globalSelectedShape.assetCode}
                            <FontAwesomeIcon
                              icon={faUpRightFromSquare}
                              className="external-link"
                            />
                          </button>
                        </div>
                        <div className="d-flex tooltip-fs">
                          <div className="tooltip-fs">Display Name: </div>
                          <div className="ms-2 tooltip-fs">
                            {globalSelectedShape.sensorDisplayName}{" "}
                          </div>
                        </div>
                        <div className="d-flex tooltip-fs">
                          <div className="tooltip-fs">Location: </div>
                          <div className="ms-2 tooltip-fs">
                            {globalSelectedShape.displayLocationName}{" "}
                          </div>
                        </div>
                        <div className="d-flex tooltip-fs">
                          <div className="tooltip-fs">Light Type: </div>
                          <div className="ms-2 tooltip-fs">
                            {globalSelectedShape.lightType}{" "}
                          </div>
                        </div>

                        <div className="d-flex tooltip-fs">
                          <div className="tooltip-fs">Last Heartbeat: </div>
                          <div className="ms-2 tooltip-fs">
                            {globalSelectedShape?.lastOnline
                              ? formatDateTime(globalSelectedShape?.lastOnline)
                              : "-"}
                          </div>
                        </div>

                        {globalSelectedShape?.assetTypeId != 3 ? (
                          <div className="d-flex tooltip-fs">
                            <div className="tooltip-fs">Motion Count: </div>
                            <div className="ms-2 tooltip-fs">
                              {isLoadingMotionAndDaylightValue ? (
                                <Spinner
                                  animation="border"
                                  variant="primary"
                                  size="sm"
                                  className="ms-4"
                                />
                              ) : (
                                <>
                                  {globalSelectedShape?.motionCount ?? "N/A"}
                                </>
                              )}
                            </div>
                          </div>
                        ) : null}

                        <div className="d-flex tooltip-fs">
                          <div className="tooltip-fs">Daylight: </div>
                          <div className="ms-2 tooltip-fs">
                            {isLoadingMotionAndDaylightValue ? (
                              <Spinner
                                animation="border"
                                variant="primary"
                                size="sm"
                                className="ms-4"
                              />
                            ) : (
                              <>
                                {globalSelectedShape?.daylight == null
                                  ? "-"
                                  : globalSelectedShape?.daylight + " Lux" || 0}
                              </>
                            )}
                          </div>
                        </div>

                        {/* <div className="d-flex justify-content-between">
                          <div className="d-flex">
                            <div className="tooltip-fs">Dim Level: </div>
                            <div className="ms-2 tooltip-fs">
                              {globalSelectedShape.dimLevel}{" "}
                            </div>
                          </div>
                          <div
                            style={{ color: "1976D2", cursor: "pointer" }}
                            onClick={() => {
                              const newTab = window.open(
                                `/asset/${btoa(
                                  globalSelectedShape.assetCode
                                )}/action/light-parameters/lighting-parameters`,
                                "_blank"
                              );

                              newTab?.focus();
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faGear}
                              className="fs-5 ms-2"
                            />
                          </div>
                        </div> */}
                        {/* <div className="d-flex justify-content-between">
                          <div className="d-flex">
                            <div className="tooltip-fs">Bright Level: </div>
                            <div className="ms-2 tooltip-fs">
                              {globalSelectedShape.brightLevel}{" "}
                            </div>
                          </div>
                          <div
                            style={{ color: "1976D2", cursor: "pointer" }}
                            onClick={() => {
                              const newTab = window.open(
                                `/asset/${btoa(
                                  globalSelectedShape.assetCode
                                )}/action/light-parameters/lighting-parameters`,
                                "_blank"
                              );

                              newTab?.focus();
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faGear}
                              className="fs-5 ms-2"
                            />
                          </div>
                        </div> */}
                        {globalSelectedShape?.status == "Fault" ? (
                          <div className="d-flex tooltip-fs">
                            <div className="tooltip-fs">Fault Type: </div>
                            <div className="ms-2 tooltip-fs">
                              {globalSelectedShape.faultType}{" "}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div style={{ width: "24px", position: "relative" }}>
                        <button
                          type="button"
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            cursor: "pointer",
                            fontSize: "22px",
                            color: "red",
                            padding: 0,
                            border: "none",
                            backgroundColor: "transparent",
                          }}
                          onClick={() => setGlobalSelectedShape(null)}
                          title="Close"
                          aria-label="Close"
                        >
                          <img
                            width={"24px"}
                            src={closeButton}
                            alt="close Button"
                          />
                          {/* <FontAwesomeIcon
                              icon={faTimes}
                              className="fs-7 ms-2"
                            /> */}
                        </button>
                      </div>
                    </div>
                    <div className="floorTooltipArrawContainer"> </div>
                  </div>
                )}
              <div
                className="flex justify-end w-100"
                style={{
                  marginTop: handle?.active
                    ? "-18rem"
                    : zoomLevel === 1
                      ? "-10rem"
                      : "-12rem",
                  paddingRight: "1rem",
                }}
              >
                <div className="d-flex flex-col align-items-end">
                  {zoomLevel !== 1 && (
                    <div>
                      <button
                        className="btn pb-0 "
                        style={{ marginRight: "-0.28rem", padding: "0.5rem" }}
                        onClick={handleResetZoom}
                        title="Reset Zoom"
                      >
                        <FontAwesomeIcon
                          icon={faRotateRight}
                          style={{ fontSize: "1.35rem" }}
                        />
                      </button>
                    </div>
                  )}
                  <div>
                    <button
                      className={"cursor-pointer ms-2"}
                      style={{ 
                        width: "25px",
                        marginBottom: "5px", 
                        padding: 0, 
                        border: "none", 
                        backgroundColor: "transparent", 
                        display: "flex", 
                      }}
                      type="button"
                      onClick={handleZoomIn}
                      title="Zoom In"
                      aria-label="Zoom In"
                    >
                      <img
                        src={zoomIn}
                        alt=""
                        aria-hidden="true"
                        style={{ width: "100%", height: "100%" }}
                      />
                    </button>
                  </div>
                  <div>
                  <button
                      className={"cursor-pointer ms-2"}
                      style={{ 
                        width: "25px",
                        marginBottom: "6px", 
                        padding: 0, 
                        border: "none", 
                        backgroundColor: "transparent", 
                        display: "flex", 
                      }}
                      type="button"
                      onClick={handleZoomOut}
                      title="Zoom Out"
                      aria-label="Zoom Out"
                    >
                      <img
                        src={zoomOut}
                        alt=""
                        aria-hidden="true"
                        style={{ width: "100%", height: "100%" }}
                      />
                    </button>
                  </div>

                  <div className="position-relative mb-1">
                    <FontAwesomeIcon
                      icon={faLayerGroup}
                      className="cursor-pointer ms-2"
                      style={{
                        height: "22px",
                        width: "22px",
                        marginRight: "3px",
                      }}
                      title="Layer Settings"
                      onClick={() => setShowLayerDropdown(!showLayerDropdown)}
                    />

                    {showLayerDropdown && (
                      <div
                        className="layer-dropdown shadow-lg"
                        style={{
                          position: "absolute",
                          top: "2rem",
                          right: 0,
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "0.75rem 1rem",
                          width: "220px",
                          zIndex: 9999,
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-semibold">Show Assets</span>
                          <B2Form.Group>
                            <B2Switch
                              type="switch"
                              name="showAssets"
                              onChange={(e: any) => {
                                setShowAssets(e.target.checked);
                                if (!e.target.checked) {
                                  setShowLabels(false);
                                }
                              }}
                              checked={showAssets}
                              value={showAssets}
                            />
                          </B2Form.Group>
                        </div>

                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold">Show Labels</span>
                          <B2Form.Group>
                            <B2Switch
                              type="switch"
                              name="showLabels"
                              onChange={(e: any) =>
                                setShowLabels(e.target.checked)
                              }
                              checked={showLabels}
                              value={showLabels}
                              disabled={!showAssets}
                            />
                          </B2Form.Group>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <span className="fw-semibold">Prevent Overlap</span>
                          <B2Form.Group>
                            <B2Switch
                              type="switch"
                              name="preventLabelOverlap"
                              onChange={(e: any) =>
                                setPreventLabelOverlap(e.target.checked)
                              }
                              checked={preventLabelOverlap}
                              value={preventLabelOverlap}
                              disabled={!showAssets || !showLabels}
                            />
                          </B2Form.Group>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    {isLoadingLocationBlockDetails && (
                      <Spinner
                        animation="border"
                        variant="primary"
                        size="sm"
                        className="me-2 mb-2"
                      />
                    )}
                    <FontAwesomeIcon
                      icon={faDownload}
                      className="cursor-pointer"
                      style={{
                        marginRight: "5px",
                        marginBottom: "6px",
                        height: "18px",
                        width: "18px",
                      }}
                      title="Export As PDF"
                      onClick={() => {
                        dispatch(getLocationBlockByCode({ blockCode })).then(
                          () => {
                            handleExportPDF(showLabels, showAssets);
                          },
                        );
                      }}
                    />
                  </div>

                  <div>
                    {handle?.active ? (
                      <button
                        className={"cursor-pointer"}
                        style={{
                          marginRight: "5px",
                          marginLeft: "10px",
                          marginBottom: "16px",
                          height: "18px",
                          width: "18px",
                          background: "none",
                          border: "none",
                          padding: "0",
                        }}
                        onClick={() => {
                          handle.exit();
                        }}
                        title="Exit Full Screen"
                      >
                        <img
                          src={exitFullScreen}
                          alt="Exit Full Screen"
                          style={{
                            height: "18px",
                            width: "18px",
                          }}
                        />
                      </button>
                    ) : (
                      <button
                        className={"cursor-pointer"}
                        style={{
                          marginRight: "5px",
                          marginLeft: "10px",
                          marginBottom: "16px",
                          height: "18px",
                          width: "18px",
                          background: "none",
                          border: "none",
                          padding: "0",
                        }}
                        onClick={() => {
                          handle.enter();
                          setZoomLevel(1.5);
                          setContainerDimensions({
                            height: window.innerHeight + 100,
                            width: window.innerWidth - 100,
                          });
                          setGlobalSelectedShape(null);
                        }}
                        title="Full Screen"
                      >
                        <img
                          src={fullScreen}
                          alt="Full Screen"
                          style={{
                            height: "18px",
                            width: "18px",
                          }}
                        />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {hovered && hoverState.visible && selectedWhileHovered && (
                <div>
                  <div
                    className="hovered-tooltip-container"
                    style={{
                      position: "absolute",
                      top: hoverState.y - 20,
                      left: hoverState.x - 100,
                    }}
                  >
                    <div className="d-flex hovered-tooltip-text">
                      <div className="hovered-tooltip-text">Display Name: </div>
                      <div className="ms-2 hovered-tooltip-text">
                        {" "}
                        {selectedWhileHovered.sensorDisplayName}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </FullScreen>
        ) : (
          <div className="no-preview-container">
            <h5>No Floor Plan to Preview</h5>
          </div>
        )}
      </SkeletonComponent>
    </div>
  );
};

export default FloorPlanHeatmapComponent;
